<template>
  <div class="student-view-container">
    <!-- 筛选区域 -->
    <filter-selector
      ref="filterSelector"
      :teacherId="$parent.teacherId"
      :institutionId="$parent.institutionId"
      :roles="$parent.roles"
      :includeLessonSelector="false"
      :initial-filters="initialFilters"
      @filter-change="handleFilterChange"
      @classes-loaded="handleClassesLoaded"
    />

    <!-- 导出按钮 -->
    <div class="action-bar" v-if="filters.classTime || isSearched">
      <a-button type="primary" @click="handleExportExcel">
        <a-icon type="download" />
        导出当前表格
      </a-button>
      <a-button
        type="primary"
        v-click-log="{ pathName: 'index', routeName: 'lessondata', btnId: 'id-1' }"
        @click="handleBatchPrint"
        style="margin-left: 10px"
      >
        <a-icon type="printer" />
        批量打印学生错题
      </a-button>
    </div>

    <!-- 表格展示区域 -->
    <div class="table-container" v-if="filters.classTime || isSearched">
      <a-spin :spinning="isLoading">
        <a-table
          :columns="columns"
          :dataSource="tableData"
          :pagination="false"
          :scroll="{ x: 1200, y: '40vh' }"
          :rowKey="record => record.id"
          bordered
        >
          <template slot="index" slot-scope="text, record, index">
            {{ index + 1 }}
          </template>

          <template slot="name" slot-scope="text, record">
            <a class="student-link" @click="viewStudentDetail(record)">{{ text }}</a>
          </template>

          <template slot="action" slot-scope="text, record">
            <div class="vertical-actions">
              <a
                class="student-link"
                v-click-log="{ pathName: 'index', routeName: 'lessondata', btnId: 'id-2' }"
                @click="printMistakes(record)"
              >打印错题</a>
            </div>
          </template>

          <template slot="lesson_data" slot-scope="text">
            <template v-if="text && text.data.state === 1">
              <div class="lesson-stats">
                <div class="first-rate">首答正确率：{{ text.data.firstCorrectRate }}%</div>
                <div class="overall-rate">含订正正确率：{{ text.data.overallCorrectRate }}%</div>
                <div v-if="text.data.title && text.data.title !== ''" class="title">称号：{{ text.data.title || '--' }}</div>
              </div>
            </template>
            <span v-else>--</span>
          </template>
        </a-table>
      </a-spin>
    </div>

    <!-- 搜索无结果展示 -->
    <div v-else-if="isSearched && !tableData.length" class="empty-search">
      <a-empty description="未找到符合条件的学生" />
    </div>

    <!-- 初始空状态提示 -->
    <div v-else class="empty-state">
      <a-empty description="请选择筛选条件或搜索学员" />
    </div>

    <!-- 学生详情弹窗 -->
    <a-modal
      v-model="studentDetailVisible"
      title="学生答题详情"
      width="900px"
      :footer="null"
    >
      <template v-if="currentStudent">
        <div class="student-detail-header">
          <div class="student-name">{{ currentStudent.name }}</div>
        </div>

        <div class="student-detail-content">
          <div class="lesson-detail">
            <template v-if="currentLessonId">
              <div class="lesson-detail-header">
                <div class="lesson-title">第{{ currentLessonNumber }}讲 {{ currentLessonName }}</div>
                <div class="lesson-stats">
                  <div>首答正确率: {{ currentLessonStats.firstCorrectRate }}%</div>
                  <div>含订正正确率: {{ currentLessonStats.overallCorrectRate }}%</div>
                  <div>获得称号: {{ currentLessonStats.title || '--' }}</div>
                </div>
              </div>

              <div class="question-list">
                <div v-for="question in questionList" :key="question.id" class="question-item">
                  <div class="question-header">
                    <div class="question-title">{{ question.title }}</div>
                    <div class="question-stats">
                      <div>首答: {{ question.firstAnswerCorrect ? '正确' : '错误' }}</div>
                      <div>最终: {{ question.finalAnswerCorrect ? '正确' : '错误' }}</div>
                      <div>用时: {{ question.timeUsed }}</div>
                    </div>
                  </div>

                  <div class="question-content">
                    <div class="question-text">{{ question.text }}</div>
                    <div class="student-answer">
                      <div>学生答案: {{ question.studentAnswer }}</div>
                      <div>正确答案: {{ question.correctAnswer }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="no-lesson-selected">
              请选择一个讲次查看详情
            </div>
          </div>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script>
import {
  Table, Icon, Modal, Empty, Button, Spin, message,
} from 'ant-design-vue';
import S from '@/service';
import FilterSelector from './common/FilterSelector.vue';

export default {
  name: 'StudentView',
  components: {
    ATable: Table,
    AIcon: Icon,
    AModal: Modal,
    AEmpty: Empty,
    AButton: Button,
    ASpin: Spin,
    FilterSelector,
  },
  props: {
    institutionId: {
      type: [String, Number],
      default: null,
    },
    teacherId: {
      type: [String, Number],
      default: null,
    },
    roles: {
      type: Array,
      default: () => [],
    },
    initialFilters: {
      type: Object,
      default: () => ({
        term: '',
        grade: '',
        classTime: '',
      }),
    },
  },
  data() {
    return {
      // 筛选条件
      filters: {
        term: '',
        grade: '',
        classTime: '',
      },
      // 搜索相关
      searchValue: '',
      isSearched: false,
      isLoading: false,
      classLessonData: {},
      lessonIdList: [],
      // 表格数据
      tableData: [],
      // 学生详情相关
      studentDetailVisible: false,
      currentStudent: null,
      currentLessonId: null,
      currentLessonNumber: 0,
      currentLessonName: '',
      currentLessonStats: {
        firstCorrectRate: 0,
        overallCorrectRate: 0,
        title: '',
      },
      // 讲次列表
      lessonList: [],
      // 题目列表
      questionList: [],
      // 选项数据
      classOptions: [],
    };
  },
  computed: {
    lessonDataTitle() {
      const classTimeObj = JSON.parse(this.filters.classTime);
      return `${this.filters.termLabel}-${classTimeObj.className}-学生数据`;
    },
    columns() {
      const baseColumns = [
        {
          title: '序号',
          key: 'index',
          width: 60,
          fixed: 'left',
          scopedSlots: { customRender: 'index' },
        },
        {
          title: '姓名',
          dataIndex: 'name',
          key: 'name',
          width: 120,
          fixed: 'left',
          scopedSlots: { customRender: 'name' },
        },
      ];

      // 添加操作列
      const actionColumn = {
        title: '操作',
        dataIndex: 'action',
        key: 'action',
        fixed: 'right',
        width: 150,
        scopedSlots: { customRender: 'action' },
      };

      const lessonColumns = Array.from({ length: this.lessonList.length }, (_, i) => {
        const lessonNum = i + 1;
        const lessonData = (this.classLessonData && this.classLessonData[lessonNum]) || { firstAverageAccurateRate: 0, averageAccurateRate: 0 };
        // 使用JSX语法创建包含讲次和正确率的标题
        const titleJsx = <div class="header-title">
          <div class="header-title-text">第{lessonNum}讲</div>
          <div class="header-rate">班级首答正确率: {lessonData.firstAverageAccurateRate || 0}%</div>
          <div class="header-rate">班级平均正确率: {lessonData.averageAccurateRate || 0}%</div>
        </div>;

        return {
          title: titleJsx,
          dataIndex: `lesson_${lessonNum}`,
          key: `lesson_${lessonNum}`,
          width: 220,
          scopedSlots: { customRender: 'lesson_data' },
        };
      });
      return [...baseColumns, ...lessonColumns, actionColumn];
      // return [...baseColumns, ...lessonColumns];
    },
  },
  mounted() {
    // 应用初始筛选条件（如果有）
    if (this.initialFilters) {
      this.filters = { ...this.initialFilters };
      // 如果有班级信息，直接获取数据
      if (this.filters.classTime) {
        this.fetchStudentData();
      }
    }
  },
  methods: {
    handleFilterChange(filters) {
      this.filters = { ...filters };

      // 如果选择了班级，则获取学生数据
      if (this.filters.classTime) {
        this.fetchStudentData();
      } else if (!this.isSearched) {
        this.tableData = [];
      }
    },

    // 处理班级列表加载完成事件
    handleClassesLoaded(classes) {
      this.classOptions = classes;
    },

    async handleExportExcel() {
      const classValue = JSON.parse(this.filters.classTime);
      const params = {
        classId: classValue.classId,
        lessonId: this.filters.lesson,
        dataTitle: this.lessonDataTitle,
      };
      const res = await S.bk.exportMsClassLessonReviewInfo(params);
      message.success('表格导出成功', res);
    },

    handleBatchPrint() {
      message.info('正在准备批量打印...');
      try {
        // 解析班级信息
        const classValue = JSON.parse(this.filters.classTime);
        const { classId, className } = classValue;

        // 将学生列表转换为JSON字符串，并进行URL编码
        const studentList = this.tableData.map((student) => ({
          studentUserId: student.id,
          name: student.name,
        }));
        const studentListParam = encodeURIComponent(JSON.stringify(studentList));

        // 构建跳转URL
        const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
        const url = `${prefix}/dataInfo.html#/external?classId=${classId}&className=${className}&mode=multiple&studentList=${studentListParam}&fromView=student`;

        // 跳转到批量打印页面
        window.open(url, '_blank');
      } catch (error) {
        this.$message.error('批量打印跳转失败，请重试');
      }
    },

    printMistakes(record) {
      this.$message.info(`正在打印${record.name}的本讲错题`);

      // 解析班级信息
      const classValue = JSON.parse(this.filters.classTime);
      const { classId, className } = classValue;
      const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
      // 构造打开新页面的URL，直接跳转到external页面
      const url = `${prefix}/dataInfo.html#/external?classId=${classId}&className=${className}&studentUserId=${record.id}&studentName=${encodeURIComponent(record.name)}&mode=single&fromView=student`;

      window.open(url, '_blank');
    },

    viewStudentDetail(student) {
      try {
        // 解析班级信息
        const classValue = JSON.parse(this.filters.classTime);
        const { classId } = classValue;
        // 使用已经构建好的lessonList，每个项目中已包含lessonId
        const lessonListData = this.lessonList.map((lesson) => {
          // 获取当前学生的这个讲次的state（如果存在）
          const lessonData = student[`lesson_${lesson.number}`];
          const lessonState = lessonData && lessonData.data ? lessonData.data.state : undefined;
          return {
            id: lesson.id,
            number: lesson.number,
            name: `第${lesson.number}讲`,
            hasData: lesson.hasData,
            lessonId: lesson.lessonId,
            state: lessonState,
          };
        });
        // 构建URL参数
        const prefix = process.env.NODE_ENV === 'development' ? '' : '/next';
        const url = `${prefix}/dataInfo.html#/detail?classId=${classId}&studentUserId=${student.id}&studentName=${student.name}&lessonList=${encodeURIComponent(JSON.stringify(lessonListData))}&fromView=student`;
        window.open(url, '_blank');
      } catch (error) {
        this.$message.error('跳转失败，请重试');
      }
    },

    async fetchStudentData() {
      try {
        this.isLoading = true;
        const classValue = JSON.parse(this.filters.classTime);
        const { classId } = classValue;
        const params = {
          classId,
        };
        const response = await S.bk.getMsClassLessonReviewInfo(params);
        this.isLoading = false;
        // 检查响应数据是否存在
        if (response && response.studentReviewInfoVoList && response.classLessonReviewInfoVoList) {
          // 根据接口返回的数据结构解析并处理数据
          this.tableData = this.formatResponseData(response);
          this.lessonIdList = response.lessonIdList || [];
          this.lessonList = this.lessonIdList.map((lessonId, index) => ({
            id: lessonId,
            number: index + 1,
            name: `第${index + 1}讲`,
            lessonId,
          }));
        } else {
          this.$message.error('获取数据失败: 数据格式不正确');
          this.tableData = [];
        }
      } catch (error) {
        this.isLoading = false;
        this.$message.error('获取数据异常');
        this.tableData = [];
      }
    },

    updateLessonHeaders(classLessonInfo) {
      // 将班级讲次数据存储为类属性，以便computed属性使用
      this.classLessonData = classLessonInfo.reduce((acc, lesson) => {
        if (lesson.lessonNum) {
          acc[lesson.lessonNum] = {
            firstAverageAccurateRate: lesson.firstAverageAccurateRate || 0,
            averageAccurateRate: lesson.averageAccurateRate || 0,
          };
        }
        return acc;
      }, {});
    },

    formatResponseData(data) {
      // 获取班级每个讲次的平均正确率信息
      const classLessonInfo = data.classLessonReviewInfoVoList || [];
      // 更新列标题中的班级平均正确率数据
      this.updateLessonHeaders(classLessonInfo);
      // 处理学生数据
      return (data.studentReviewInfoVoList || []).map((student) => {
        const studentData = {
          id: student.studentUserId,
          name: student.studentName,
        };

        // 处理每个讲次的数据
        (student.studentLessonReviewDetailInfoVoList || []).forEach((lessonDetail, index) => {
          // 索引+1作为讲次编号
          const lessonNum = index + 1;
          // 如果该讲次有数据
          if (lessonDetail) {
            studentData[`lesson_${lessonNum}`] = {
              data: {
                firstCorrectRate: lessonDetail.firstLessonAccuracy || 0,
                overallCorrectRate: lessonDetail.lessonAccuracy || 0,
                title: lessonDetail.tag || '',
                state: lessonDetail.state,
              },
            };
          } else {
            studentData[`lesson_${lessonNum}`] = null;
          }
        });

        return studentData;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.student-view-container {
  .action-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }

  .vertical-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    a {
      display: block;
      margin-bottom: 8px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .student-view-container ::v-deep .ant-table-thead {
    border-radius: 0;
  }

  ::v-deep .ant-table-fixed-left,
  ::v-deep .ant-table-fixed-right {
    z-index: 0 !important;
    border-radius: 0;
    box-shadow: none;
  }

  ::v-deep .ant-table-fixed-right {
    right: 0;
    // border-right: 1px solid #e8e8e8;
  }

  ::v-deep .ant-table-body-outer {
    height: calc(40vh - 6px) !important;
    max-height: calc(40vh - 6px) !important;
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }

  .table-container {
    .student-link {
      color: #1890ff;
      cursor: pointer;

      &:hover {
        color: #40a9ff;
        // text-decoration: underline;
      }
    }
    ::v-deep .ant-table-body {
      &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
      }
    }
    ::v-deep .ant-table-thead > tr > th {
      background-color: #f2f7fc;
      text-align: center;
      border-radius: 0;
    }

    ::v-deep .ant-table-tbody > tr > td:not(:last-child) {
      border-right: 1px solid #e8e8e8;
    }

    ::v-deep .ant-table-thead > tr > th:last-child,
    ::v-deep .ant-table-tbody > tr > td:last-child {
      border-right: none;
    }

    ::v-deep .ant-table-fixed-left .ant-table-tbody > tr > td {
      border-right: 1px solid #e8e8e8;
    }

    ::v-deep .ant-table-thead > tr > th.custom-action-header {
      background-color: #ffffff;
    }

    ::v-deep .ant-table-header {
      background-color: #ffffff;
      box-shadow: none;
    }

    ::v-deep .ant-table-tbody > tr > td {
      text-align: center;
    }

    .lesson-stats {
      display: flex;
      flex-direction: column;
      gap: 5px;
      font-size: 12px;

      .first-rate {
        color: #0063f7;
      }

      .overall-rate {
        color: #06c270;
      }

      .title {
        color: #fdac42;
      }
    }
  }

  .empty-state, .empty-search {
    padding: 40px 0;
    text-align: center;
  }

  // 学生详情弹窗样式
  .student-detail-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e8e8e8;

    .student-name {
      font-size: 18px;
      font-weight: bold;
      color: #303858;
    }
  }

  .student-detail-content {
    display: flex;
    height: 500px;

    .lesson-list {
      width: 220px;
      border-right: 1px solid #e8e8e8;
      overflow-y: auto;
      padding-right: 10px;

      .lesson-item {
        padding: 10px 15px;
        margin-bottom: 8px;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
          background-color: #f5f7f9;
        }

        &.active {
          background-color: #e6f7ff;
          color: #1890ff;
          font-weight: bold;
        }

        &.disabled {
          color: #c1c6d0;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
          }
        }
      }
    }

    .lesson-detail {
      flex: 1;
      padding-left: 20px;
      overflow-y: auto;

      .lesson-detail-header {
        margin-bottom: 20px;
        .lesson-title {
          font-size: 16px;
          font-weight: bold;
          color: #303858;
          margin-bottom: 10px;
        }
        .lesson-stats {
          display: flex;
          gap: 20px;
          color: #505873;
        }
      }

      .question-list {
        .question-item {
          margin-bottom: 20px;
          padding: 15px;
          background-color: #f5f7f9;
          border-radius: 8px;
          .question-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            .question-title {
              font-weight: bold;
              color: #303858;
            }
            .question-stats {
              display: flex;
              gap: 15px;
              color: #505873;
            }
          }
          .question-content {
            .question-text {
              margin-bottom: 10px;
              color: #303858;
            }
            .student-answer {
              display: flex;
              gap: 20px;
              color: #505873;
            }
          }
        }
      }

      .no-lesson-selected {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        color: #a3b2c2;
        font-size: 16px;
      }
    }
  }
}

/* 表头标题样式 */
.header-title {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.header-title-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

/* 表头正确率样式 */
.header-rate {
  font-size: 10px;
  font-weight: 400;
  color: #666;
}
</style>
